import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useReducedMotion,
} from "framer-motion";

const STATS = [
    { value: "Malaysia", label: "Strategic Base" },
    { value: "ASEAN", label: "Regional Reach" },
    { value: "Global", label: "Network" },
];

export const About = () => {
    const ref = useRef(null);
    const reduce = useReducedMotion();
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    const imageY = useTransform(scrollYProgress, [0, 1], ["-10%", "15%"]);
    const captionY = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);

    return (
        <section
            ref={ref}
            id="about"
            data-testid="about-section"
            className="relative bg-[#0B0807]/82 backdrop-blur-md py-24 sm:py-32 lg:py-40 px-4 sm:px-8 lg:px-12"
        >
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Section label */}
                <div className="lg:col-span-12 mb-6">
                    <div className="flex items-center gap-3 font-mono-text text-[10px] sm:text-xs uppercase tracking-[0.3em] text-zinc-500">
                        <span className="h-px w-10 bg-[#D69331]" />
                        <span>01 / Company Overview</span>
                    </div>
                </div>

                {/* Headline */}
                <motion.div
                    initial={{ y: 60, opacity: 0 }}
                    whileInView={{ y: 0, opacity: 1 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="lg:col-span-12"
                >
                    <h2 className="font-display uppercase font-bold leading-[0.9] tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl xl:text-8xl">
                        Building stronger connections{" "}
                        <span className="text-zinc-600">
                            between global markets.
                        </span>
                    </h2>
                </motion.div>

                {/* Body text + media */}
                <div className="lg:col-span-7 mt-12 lg:mt-16 space-y-6">
                    <motion.p
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-mono-text text-base sm:text-lg text-zinc-300 leading-relaxed max-w-2xl"
                    >
                        <span className="text-white font-medium">
                            DUHA GLOBAL TRADING SDN. BHD.
                        </span>{" "}
                        is a Malaysia-based international trading, FMCG
                        distribution, halal sourcing, import-export and supply
                        chain solutions company dedicated to creating efficient
                        global trade connections between suppliers,
                        distributors, retailers and consumers.
                    </motion.p>

                    <motion.p
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                        className="font-mono-text text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl"
                    >
                        With a strategic presence in Malaysia and access to
                        major international markets, we help businesses expand
                        beyond borders through reliable sourcing, professional
                        logistics coordination, market development support and
                        sustainable trade solutions.
                    </motion.p>

                    <motion.p
                        initial={{ y: 30, opacity: 0 }}
                        whileInView={{ y: 0, opacity: 1 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.7, delay: 0.3 }}
                        className="font-mono-text text-sm sm:text-base text-zinc-400 leading-relaxed max-w-2xl"
                    >
                        Our business model is designed around flexibility,
                        scalability and efficiency. Rather than heavy
                        infrastructure investments, we use a strategic{" "}
                        <span className="text-[#D69331]">asset-light</span>{" "}
                        approach supported by trusted logistics providers,
                        warehousing partners, manufacturers, distributors and
                        fulfillment operators.
                    </motion.p>
                </div>

                <div className="lg:col-span-5 mt-12 lg:mt-16">
                    <div className="relative overflow-hidden aspect-[4/5] border border-white/10">
                        <motion.img
                            src="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1400&q=76"
                            alt="International trade container port operations"
                            className="absolute inset-0 w-full h-[120%] object-cover"
                            style={reduce ? {} : { y: imageY }}
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0807] via-transparent to-transparent" />
                        <motion.div
                            style={reduce ? {} : { y: captionY }}
                            className="absolute bottom-0 left-0 right-0 p-5 border-t border-white/10 bg-black/50 backdrop-blur-sm"
                        >
                            <p className="font-mono-text text-[10px] uppercase tracking-[0.25em] text-[#D69331] mb-1">
                                Coordinated movement
                            </p>
                            <p className="font-mono-text text-xs sm:text-sm text-zinc-300">
                                Compliant routing, dependable regional execution.
                            </p>
                        </motion.div>
                    </div>
                </div>

                {/* Stats row */}
                <div className="lg:col-span-12 mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
                    {STATS.map((s, i) => (
                        <motion.div
                            key={s.label}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.1 }}
                            className="bg-[#0B0807] p-8 sm:p-10 group hover:bg-[#0f0f0f] transition-colors"
                        >
                            <div className="font-display uppercase text-3xl sm:text-4xl lg:text-5xl font-bold text-white group-hover:text-[#D69331] transition-colors">
                                {s.value}
                            </div>
                            <div className="mt-3 font-mono-text text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                                {s.label}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
